import { isNative } from '@unifyapps/ui/utils/isNative';
import { type ReactNode, useMemo } from 'react';
import { useAppDependencyGraphProvider } from '../../../../context/DependencyGraphProvider';
import {
  useGetAppComputeContext,
  useNonReactiveAppComputeContext,
  useReactiveAppComputeContext,
} from '../../../../hooks/computeContext';
import { useAppBlockEvents } from '../../../../hooks/useBlockEvents';
import { APP_MANAGER_ID } from '../../../../pulse/scopeRegistry';
import { useGlobalAppStateStore } from '../../../../stores/app/GlobalAppStateStore';
import { useAppDataSourceRecordStore } from '../../../../stores/DataSourceRecordStore';
import { useInterfaceAppStore } from '../../../../stores/InterfaceStore';
import { AggregationMetadataHandler } from '../../components/AggregationMetadataHandler';
import { GLOBAL_PAGE_AGGREGATE_METADATA_DATA_SOURCE_RESOURCE_NAME } from '../../components/AggregationMetadataHandler/utils';
import { PreviewDataSourceHandler } from '../../components/PreviewDataSourceHandler';
import type { DataSourceControllerContextType } from '../../context/DataSourceControllerContext';
import { DataSourceControllerProvider } from '../../context/DataSourceControllerProvider';
import { AutomaticDataSourceController } from '../AutomaticDataSourceController';
import { ManualDataSourceController } from '../ManualDataSourceController';
import { RefetchDataSourceController } from '../RefetchDataSourceController';

function AppDataSourceControllerProvider({ children }: { children: ReactNode }) {
  const { getAppComputeContext } = useGetAppComputeContext();
  const mode = useGlobalAppStateStore().hooks.useMode();
  const activePageSlug = useInterfaceAppStore().hooks.page.useSlug();
  const activePageId = useInterfaceAppStore().hooks.page.useId();
  const isPublicPage = useInterfaceAppStore().hooks.page.useIsPublicPage();
  const useDataSourceRecordStore = useAppDataSourceRecordStore();
  const useDependencyGraphProvider = useAppDependencyGraphProvider();
  const useGlobalStateStore = useGlobalAppStateStore();
  const interfaceRecord = useGlobalAppStateStore().hooks.record.useDetails();

  const contextValue = useMemo<DataSourceControllerContextType>(
    () => ({
      isPublicPage,
      allowedDataSourceResourceNames: GLOBAL_PAGE_AGGREGATE_METADATA_DATA_SOURCE_RESOURCE_NAME,
      mode,
      interfaceRecord,
      getComputeContext: getAppComputeContext,
      // for module send page id instead.
      activePageSlug: activePageSlug ?? activePageId,
      useNonReactiveComputeContext: useNonReactiveAppComputeContext,
      useReactiveComputeContext: useReactiveAppComputeContext,
      managerId: APP_MANAGER_ID,
      useDataSourceRecordStore,
      useGlobalStateStore,
      useDependencyGraphProvider,
      useBlockEvents: useAppBlockEvents,
    }),
    [
      getAppComputeContext,
      mode,
      useDataSourceRecordStore,
      useDependencyGraphProvider,
      useGlobalStateStore,
      interfaceRecord,
      isPublicPage,
      activePageSlug,
      activePageId,
    ],
  );
  return (
    <DataSourceControllerProvider value={contextValue}>{children}</DataSourceControllerProvider>
  );
}

function AppDataSourceController({ children }: { children: React.ReactNode }) {
  return (
    <>
      <AppDataSourceControllerProvider>
        {isNative() ? null : <AggregationMetadataHandler />}
        <AutomaticDataSourceController />
        <ManualDataSourceController />
        <PreviewDataSourceHandler />
        <RefetchDataSourceController />
      </AppDataSourceControllerProvider>
      {children}
    </>
  );
}

export default AppDataSourceController;
